/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { INTERNAL_LINKS } from '../../utils';
import { Button } from '../design-system';
import { LinkWithQuery } from '../global/LinkWithQuery';

const Wrapper = styled.div`
  position: relative;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin: 0 0 30px 0;
  }
`;

const ProgressBar = styled.div<{
  $hideBorder: boolean;
  $totalOptions: number;
}>`
  border-left: ${({ $hideBorder, theme }) =>
    $hideBorder ? `unset` : `1px solid ${rgba(theme.highlightColor, 0.35)}`};
  position: absolute;
  pointer-events: none;
  width: 100%;
  height: 100%;
`;

const Option = styled.div`
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.MEDIUM};
  line-height: 30px;
  transition: opacity ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};
  margin-left: 15px;
  opacity: 0.6;
  text-wrap: nowrap;

  &:hover {
    opacity: 1;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    margin-left: 0;
    margin-right: 15px;
    opacity: 1;
  }
`;

const OptionWrapper = styled.div<{ $thisCategory: boolean }>`
  background: ${({ $thisCategory }) =>
    $thisCategory
      ? `linear-gradient(
    90deg,
    rgba(245, 192, 192, 0.098) 0%,
    rgba(245, 192, 192, 0) 100%
  )`
      : 'unset'};
  transition: top 250ms ease-in-out;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    background: transparent;
  }
`;

const StyledLinkWithQuery = styled(LinkWithQuery)`
  border-bottom: none;

  &:hover {
    background-color: transparent;
  }
`;

interface UseCaseNavigationProps {
  thisCategory?: string | undefined;
}

export interface UseCaseCategory {
  slug: string;
  title: string;
}

interface UseCasesPageData {
  allContentfulUseCaseCategory: {
    nodes: UseCaseCategory[];
  };
}

export const UseCaseNavigation: React.ComponentType<UseCaseNavigationProps> =
  React.memo(({ thisCategory }) => {
    const data: UseCasesPageData = useStaticQuery(pageQuery);
    const categories = data.allContentfulUseCaseCategory.nodes;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.mediaQueryString.MEDIUM);

    return (
      <Wrapper>
        <ProgressBar
          $hideBorder={!thisCategory}
          $totalOptions={categories.length}
        />
        <StyledLinkWithQuery to={INTERNAL_LINKS.Templates}>
          {isMobile ? (
            <Button
              link={INTERNAL_LINKS.Templates}
              size="small"
              stylez={!thisCategory ? 'default' : 'minimal'}
              text="All"
            />
          ) : (
            <OptionWrapper $thisCategory={!thisCategory}>
              <Option>All</Option>
            </OptionWrapper>
          )}
        </StyledLinkWithQuery>
        {categories.map((category, index) => (
          <StyledLinkWithQuery
            key={index}
            to={`${INTERNAL_LINKS.Templates}${category.slug}/`}>
            {isMobile ? (
              <Button
                link={`${INTERNAL_LINKS.Templates}${category.slug}/`}
                size="small"
                stylez={thisCategory === category.title ? 'default' : 'minimal'}
                text={category.title}
              />
            ) : (
              <OptionWrapper $thisCategory={thisCategory === category.title}>
                <Option>{category.title}</Option>
              </OptionWrapper>
            )}
          </StyledLinkWithQuery>
        ))}
      </Wrapper>
    );
  });

const pageQuery = graphql`
  query {
    allContentfulUseCaseCategory(sort: { title: ASC }) {
      nodes {
        title
        slug
      }
    }
  }
`;
